import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';
import { getAgeCategory, getEdition } from '../utils/subscriptionUtils';
import { useAuth } from '../../../context/AuthContext';
import { downloadCsvFile } from '../../../utils/csvDownload';

const apiBaseUrl = config.getApiUrl();

const SUB_TAB_URLS = {
  0: '/api/subscriptions/all', // Live - All subscriptions
  1: '/api/subscriptions/active', // Current Subscribers - Active subscriptions
  2: '/api/subscriptions/cancelled', // Cancelled subscriptions
  3: '/api/subscriptions/halted' // Halted subscriptions
};

export const useSubscriptions = (showSnackbar) => {
  const { user } = useAuth();
  const canFetchSubscriptions = Array.isArray(user?.tabs) &&
    (user.tabs.includes('subscriptions') || user.tabs.includes('coupon_usage'));
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [subscriptionSubTab, setSubscriptionSubTab] = useState(0);
  const [ageFilter, setAgeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Edition editing state
  const [editingEdition, setEditingEdition] = useState(null);
  const [editionValue, setEditionValue] = useState('');

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const url = `${apiBaseUrl}${SUB_TAB_URLS[subscriptionSubTab] || SUB_TAB_URLS[0]}`;
      const data = await fetchWithAuth(url);
      setSubscriptions(data);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canFetchSubscriptions) {
      setLoading(false);
      return;
    }
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionSubTab, canFetchSubscriptions]);

  const handleEditEdition = (subscriptionId, currentEdition) => {
    setEditingEdition(subscriptionId);
    setEditionValue(currentEdition || '');
  };

  const handleSaveEdition = async (subscriptionId) => {
    try {
      const response = await fetchWithAuth(`${apiBaseUrl}/api/subscriptions/${subscriptionId}/edition`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ edition: editionValue })
      });

      if (response.success) {
        showSnackbar('Edition updated successfully', 'success');
        setEditingEdition(null);
        setEditionValue('');
        fetchSubscriptions();
      } else {
        showSnackbar('Failed to update edition', 'error');
      }
    } catch (err) {
      console.error('Error updating edition:', err);
      showSnackbar('Error updating edition', 'error');
    }
  };

  const handleCancelEditionEdit = () => {
    setEditingEdition(null);
    setEditionValue('');
  };

  const filterSubscriptionsByAge = (subs) => {
    if (ageFilter === 'all') return subs;

    return subs.filter(sub => {
      const age = sub.childDetails?.age;
      if (!age) return false;
      const ageNum = parseFloat(age);

      switch (ageFilter) {
        case 'beginner':
          return ageNum >= 1 && ageNum <= 5;
        case 'junior':
          return ageNum > 5 && ageNum <= 9;
        case 'senior':
          return ageNum > 9 && ageNum <= 15;
        default:
          return true;
      }
    });
  };

  const filterSubscriptionsBySearch = (subs) => {
    if (!searchQuery.trim()) return subs;

    const query = searchQuery.toLowerCase().trim();
    return subs.filter(sub => {
      const email = (sub.email || '').toLowerCase();
      const phone = (sub.phone || '').toLowerCase();
      const name = (sub.customerName || '').toLowerCase();
      const subscriptionId = String(sub.subscriptionId || '').toLowerCase();
      const id = String(sub._id || '').toLowerCase();

      return (
        email.includes(query) ||
        phone.includes(query) ||
        name.includes(query) ||
        subscriptionId.includes(query) ||
        id.includes(query)
      );
    });
  };

  const downloadCSV = () => {
    const dataToExport = filterSubscriptionsBySearch(subscriptionSubTab === 1 ? filterSubscriptionsByAge(subscriptions) : subscriptions);

    if (dataToExport.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }

    const headers = [
      'Name',
      'Email',
      'Phone',
      'Kid\'s Age',
      'Age Category',
      'Edition',
      'Period',
      'Coupon Code',
      'Subscription ID',
      'Payment ID',
      'Status',
      'Created At',
      'Cancelled At'
    ];

    const tabName = subscriptionSubTab === 0 ? 'Live' :
                    subscriptionSubTab === 1 ? 'Current_Subscribers' :
                    subscriptionSubTab === 2 ? 'Cancelled' : 'Halted';
    const fileName = `YoungPresso_Subscriptions_${tabName}_${new Date().toISOString().split('T')[0]}.csv`;

    const count = downloadCsvFile({
      headers,
      rows: dataToExport.map((sub) => [
        sub.customerName || '',
        sub.email || '',
        sub.phone || '',
        sub.childDetails?.age || '',
        getAgeCategory(sub.childDetails?.age) || '',
        getEdition(sub) || '',
        sub.period || '',
        sub.couponCode || '',
        sub.subscriptionId || '',
        sub.paymentDetails?.razorpay_payment_id || '',
        sub.status || '',
        sub.createdAt ? new Date(sub.createdAt).toLocaleString() : 'N/A',
        sub.cancelledAt ? new Date(sub.cancelledAt).toLocaleString() : 'N/A'
      ]),
      fileName
    });

    showSnackbar(`CSV downloaded successfully (${count} records)`, 'success');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSubscriptionSubTabChange = (event, newValue) => {
    setSubscriptionSubTab(newValue);
    setPage(0); // Reset pagination when switching tabs
    setAgeFilter('all'); // Reset age filter when switching tabs
    setSearchQuery(''); // Reset search query when switching tabs
  };

  return {
    subscriptions,
    loading,
    error,
    page,
    rowsPerPage,
    subscriptionSubTab,
    ageFilter,
    setAgeFilter,
    searchQuery,
    setSearchQuery,
    editingEdition,
    editionValue,
    setEditionValue,
    fetchSubscriptions,
    handleEditEdition,
    handleSaveEdition,
    handleCancelEditionEdit,
    filterSubscriptionsByAge,
    filterSubscriptionsBySearch,
    downloadCSV,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSubscriptionSubTabChange
  };
};
